import React, { useMemo } from 'react';
import './styles.css';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  maxHeight?: string;
  /** Se true, permite interação com checkboxes e selects */
  interactive?: boolean;
  /** Callback quando um checkbox é alterado */
  onCheckboxChange?: (index: number, checked: boolean) => void;
  /** Callback quando um select é alterado */
  onSelectChange?: (id: string, value: string) => void;
}

/**
 * Processa sintaxe markdown de checkbox estilo GitHub
 * [ ] -> checkbox não marcado
 * [x] ou [X] -> checkbox marcado
 */
function processCheckboxMarkdown(html: string): string {
  // Padrão para encontrar [ ] ou [x] no início de linhas ou após tags
  const checkboxPattern = /\[([ xX])\]\s*/g;
  let index = 0;

  return html.replace(checkboxPattern, (_, checked) => {
    const isChecked = checked.toLowerCase() === 'x';
    const checkboxHtml = `<span class="inline-checkbox" data-index="${index}" data-checked="${isChecked}">
      <span class="checkbox-box ${isChecked ? 'checked' : ''}"></span>
    </span> `;
    index++;
    return checkboxHtml;
  });
}

/**
 * Processa sintaxe customizada de select inline
 * {select:Placeholder?Opção1|Opção2|Opção3}
 * {select:Opção1|Opção2|Opção3}
 * Com valor selecionado: {select:Placeholder?Opção1|*Opção2|Opção3}
 */
function processSelectMarkdown(html: string): string {
  const selectPattern = /\{select:([^}]+)\}/g;

  return html.replace(selectPattern, (_, content: string) => {
    let placeholder = '';
    let optionsStr = content;
    let selected = '';
    const selectId = `select-${Math.random().toString(36).substr(2, 9)}`;

    // Verificar se tem placeholder (separado por ?)
    if (content.includes('?')) {
      const parts = content.split('?');
      placeholder = parts[0];
      optionsStr = parts.slice(1).join('?');
    }

    // Parse das opções (separadas por |)
    const options = optionsStr.split('|').map(opt => {
      const trimmed = opt.trim();
      // Verificar se está marcado como selecionado (*)
      if (trimmed.startsWith('*')) {
        selected = trimmed.substring(1);
        return trimmed.substring(1);
      }
      return trimmed;
    }).filter(opt => opt.length > 0);

    // Gerar o HTML do select
    const optionsJson = JSON.stringify(options);
    let selectHtml = `<span class="ql-inline-select" data-options='${optionsJson}' data-selected="${selected}" data-placeholder="${placeholder}" data-select-id="${selectId}">`;
    selectHtml += `<select class="ql-inline-select-dropdown">`;

    if (placeholder) {
      selectHtml += `<option value="" disabled ${!selected ? 'selected' : ''}>${placeholder}</option>`;
    }

    options.forEach(opt => {
      const isSelected = opt === selected;
      selectHtml += `<option value="${opt}"${isSelected ? ' selected' : ''}>${opt}</option>`;
    });

    selectHtml += `</select></span>`;

    return selectHtml;
  });
}

/**
 * Processa o conteúdo HTML para adicionar suporte a checkbox e select markdown
 */
function processContent(content: string): string {
  let processed = content;
  
  // Processar checkboxes estilo GitHub
  processed = processCheckboxMarkdown(processed);
  
  // Processar selects customizados
  processed = processSelectMarkdown(processed);
  
  return processed;
}

/**
 * Componente para exibir conteúdo de texto rico (HTML) de forma segura
 * Aplica estilos consistentes para o conteúdo renderizado
 * Suporta:
 * - Checkbox estilo GitHub: [ ] e [x]
 * - Select inline: {select:Placeholder?Opção1|Opção2|Opção3}
 */
export const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  className = '',
  maxHeight,
  interactive = false,
  onCheckboxChange,
  onSelectChange,
}) => {
  // Processa o conteúdo para converter sintaxe markdown
  const processedContent = useMemo(() => {
    if (!content || content.trim() === '' || content === '<p></p>') {
      return '';
    }
    return processContent(content);
  }, [content]);

  // Se não houver conteúdo, não renderiza nada
  if (!processedContent) {
    return null;
  }

  // Handler para cliques (checkboxes)
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;

    const target = e.target as HTMLElement;
    const checkboxBox = target.closest('.checkbox-box');
    
    if (checkboxBox) {
      const checkbox = checkboxBox.closest('.inline-checkbox') as HTMLElement;
      if (checkbox) {
        const index = parseInt(checkbox.dataset.index || '0', 10);
        const currentChecked = checkbox.dataset.checked === 'true';
        onCheckboxChange?.(index, !currentChecked);
      }
    }
  };

  // Handler para mudanças em selects
  const handleChange = (e: React.ChangeEvent<HTMLDivElement>) => {
    if (!interactive) return;

    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT') {
      const select = target as HTMLSelectElement;
      const container = select.closest('.ql-inline-select') as HTMLElement;
      if (container) {
        const selectId = container.dataset.selectId || '';
        onSelectChange?.(selectId, select.value);
      }
    }
  };

  return (
    <div
      className={`rich-text-display ${className}`}
      style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      dangerouslySetInnerHTML={{ __html: processedContent }}
      onClick={handleClick}
      onChange={handleChange as unknown as React.FormEventHandler<HTMLDivElement>}
      // Desabilita tradução automática do navegador
      translate="no"
      data-google-translate="false"
      data-ms-translate="false"
    />
  );
};

export default RichTextDisplay;